import router from '@/router';
import registerService from '../services/registerService';
import apiDataSourceService from '../services/apiDataSourceService';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';

export default {
  // Register actions
  async FETCH_REGISTERS({ commit }, { facilityId, userId } = {}) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    
    try {
      const registers = await registerService.getRegisters(facilityId, userId);
      commit('SET_REGISTERS', registers.items);
      commit('SET_TOTAL', registers.total);
      return registers;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // UI-specific: fetch registers for a given user in a facility
  async FETCH_USER_FACILITY_REGISTERS({ commit }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const facilityId = router.currentRoute.params.id;
      const registers = await registerService.getUserFacilityRegisters(facilityId);
      const items = registers.items || registers;
      const total = registers.total || (Array.isArray(items) ? items.length : 0);
      commit('SET_REGISTERS', items);
      commit('SET_TOTAL', total);
      return registers;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async FETCH_REGISTER({ commit }, registerId) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    
    try {
      const register = await registerService.getRegister(registerId);
      commit('SET_SELECTED_REGISTER', register);
      return register;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async CREATE_REGISTER({ commit }, registerData) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    
    try {
      const newRegister = await registerService.createRegister(registerData);
      commit('ADD_REGISTER', newRegister);
      return newRegister;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async UPDATE_REGISTER({ commit }, { id, data }) {
    commit('SET_UPDATE_LOADING', true);
    commit('CLEAR_ERROR');
    
    try {
      const updatedRegister = await registerService.updateRegister(id, data);
      commit('UPDATE_REGISTER', updatedRegister);
      return updatedRegister;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_UPDATE_LOADING', false);
    }
  },

  async BULK_UPDATE_REGISTER_ROWS({ commit }, { registerId, rowIds, changes }) {
    commit('SET_UPDATE_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      return await registerService.bulkUpdateRegisterRows(registerId, rowIds, changes);
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_UPDATE_LOADING', false);
    }
  },

  async DELETE_REGISTER({ commit }, registerId) {
    commit('SET_UPDATE_LOADING', true);
    commit('CLEAR_ERROR');
    
    try {
      await registerService.deleteRegister(registerId);
      commit('REMOVE_REGISTER', registerId);
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_UPDATE_LOADING', false);
    }
  },

  async IMPORT_DATA({ commit }, { registerId, data, mapping, fileName }) {
    commit('SET_UPLOAD_STATUS', 'importing');
    commit('CLEAR_ERROR');

    try {
      const importResults = await registerService.importData({
        registerId,
        data,
        mapping,
        fileName
      });
      
      commit('SET_IMPORT_RESULTS', importResults);
      commit('SET_UPLOAD_STATUS', 'idle');
      
      // Refresh register data after import
      if (importResults.success) {
        const updatedRegister = await registerService.getRegister(registerId);
        commit('UPDATE_REGISTER', updatedRegister);
      }
      
      return importResults;
    } catch (error) {
      commit('SET_ERROR', error);
      commit('SET_UPLOAD_STATUS', 'idle');
      throw error;
    }
  },

  // Template actions
  async FETCH_DATA_TEMPLATES({ commit }, params = {}) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const templates = await registerService.getDataTemplates(params);
      commit('SET_TEMPLATES', templates);
      return templates;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async FETCH_DATA_TEMPLATE({ commit }, templateId) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const template = await registerService.getDataTemplate(templateId);
      return template;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async CREATE_DATA_TEMPLATE({ commit }, templateData) {
    commit('SET_UPDATE_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const template = await registerService.createDataTemplate(templateData);
      commit('ADD_TEMPLATE', template);
      return template;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_UPDATE_LOADING', false);
    }
  },

  async UPDATE_DATA_TEMPLATE({ commit }, { templateId, templateData }) {
    commit('SET_UPDATE_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const template = await registerService.updateDataTemplate(templateId, templateData);
      commit('UPDATE_TEMPLATE', template);
      return template;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_UPDATE_LOADING', false);
    }
  },

  async DELETE_DATA_TEMPLATE({ commit }, templateId) {
    commit('SET_UPDATE_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      await registerService.deleteDataTemplate(templateId);
      commit('REMOVE_TEMPLATE', templateId);
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_UPDATE_LOADING', false);
    }
  },

  async FETCH_TEMPLATE_FIELD_MAPPING({ commit }, templateId) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const mapping = await registerService.getTemplateFieldMapping(templateId);
      commit('SET_CURRENT_MAPPING', mapping);
      return mapping;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Export actions
  async EXPORT_REGISTER({ commit }, { registerId, format = 'excel' }) {
    commit('SET_UPDATE_LOADING', true);
    commit('CLEAR_ERROR');
    
    try {
      const result = await registerService.exportRegister(registerId, format);
      return result;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_UPDATE_LOADING', false);
    }
  },

  // Clear all data entries for a register
  async CLEAR_REGISTER_DATA({ commit }, registerId) {
    commit('SET_UPDATE_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const result = await registerService.clearRegisterData(registerId);
      // Optionally refresh the register or its data after clearing
      const updatedRegister = await registerService.getRegister(registerId);
      commit('UPDATE_REGISTER', updatedRegister);
      return result;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_UPDATE_LOADING', false);
    }
  },

  async FETCH_USERS({ commit }) {
    try {
      const users = await registerService.getUsers();
      commit('SET_USERS', users.items);
      return users;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    }
  },

  async UPDATE_REGISTER_SCHEMA({ commit }, { registerId, schema }) {
    commit('SET_UPDATE_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const updatedRegister = await registerService.updateRegisterSchema(registerId, schema);
      commit('UPDATE_REGISTER', updatedRegister);
      return updatedRegister;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_UPDATE_LOADING', false);
    }
  },

  // ==========================================
  // AUDIT LOG ACTIONS
  // ==========================================

  /**
   * Fetch audit logs for a register (register-wide view)
   */
  async FETCH_REGISTER_AUDIT_LOGS({ commit, state }, registerId) {
    commit('SET_AUDIT_LOGS_LOADING', true);

    try {
      const params = { ...state.auditFilters };
      // Remove null/undefined values
      Object.keys(params).forEach(key => {
        if (params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const result = await registerService.getRegisterAuditLogs(registerId, params);
      // API returns { logs, totalCount } not { items, total }
      commit('SET_AUDIT_LOGS', result.logs || result.items || []);
      commit('SET_AUDIT_LOGS_TOTAL', result.totalCount || result.total || 0);
      return result;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_AUDIT_LOGS_LOADING', false);
    }
  },

  /**
   * Fetch audit logs for a specific row
   */
  async FETCH_ROW_AUDIT_LOGS({ commit, state }, rowId) {
    commit('SET_AUDIT_LOGS_LOADING', true);

    try {
      const params = { ...state.auditFilters };
      // Remove null/undefined values
      Object.keys(params).forEach(key => {
        if (params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const result = await registerService.getRowAuditLogs(rowId, params);
      // API returns { logs, totalCount } not { items, total }
      commit('SET_AUDIT_LOGS', result.logs || result.items || []);
      commit('SET_AUDIT_LOGS_TOTAL', result.totalCount || result.total || 0);
      return result;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_AUDIT_LOGS_LOADING', false);
    }
  },

  /**
   * Fetch detailed audit log entry with all field changes
   */
  async FETCH_AUDIT_LOG_DETAIL({ commit }, logId) {
    commit('SET_AUDIT_LOG_DETAIL_LOADING', true);

    try {
      const detail = await registerService.getAuditLogDetail(logId);
      commit('SET_AUDIT_LOG_DETAIL', detail);
      return detail;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_AUDIT_LOG_DETAIL_LOADING', false);
    }
  },

  /**
   * Clear audit logs for a register (admin only)
   */
  async CLEAR_AUDIT_LOGS({ commit, dispatch }, { registerId, olderThan = null }) {
    commit('SET_AUDIT_LOGS_LOADING', true);

    try {
      const result = await registerService.clearAuditLogs(registerId, olderThan);
      // Refresh the audit logs after clearing
      await dispatch('FETCH_REGISTER_AUDIT_LOGS', registerId);
      return result;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_AUDIT_LOGS_LOADING', false);
    }
  },

  /**
   * Open the audit sidebar in register mode
   * @param {Object|string} payload - Either { registerId } object or just the registerId string
   */
  OPEN_AUDIT_SIDEBAR_REGISTER({ commit, dispatch }, payload) {
    // Handle both object { registerId } and direct string formats
    const registerId = typeof payload === 'object' ? payload.registerId : payload;

    if (!registerId) {
      debugWarn('OPEN_AUDIT_SIDEBAR_REGISTER: No registerId provided');
      return;
    }

    commit('SET_AUDIT_SIDEBAR_MODE', 'register');
    commit('SET_AUDIT_REGISTER_ID', registerId); // Store registerId for filter updates
    commit('SET_SELECTED_ROW_FOR_AUDIT', null);
    commit('SET_AUDIT_SIDEBAR_OPEN', true);
    commit('RESET_AUDIT_FILTERS');
    dispatch('FETCH_REGISTER_AUDIT_LOGS', registerId);
  },

  /**
   * Open the audit sidebar in row mode
   */
  OPEN_AUDIT_SIDEBAR_ROW({ commit, dispatch }, { row, registerId }) {
    // Note: row.Id uses capital I (from API response)
    const rowId = row.Id || row.id;

    if (!rowId) {
      debugWarn('OPEN_AUDIT_SIDEBAR_ROW: No row ID provided');
      return;
    }

    commit('SET_AUDIT_SIDEBAR_MODE', 'row');
    commit('SET_AUDIT_REGISTER_ID', registerId); // Store registerId for filter updates
    commit('SET_SELECTED_ROW_FOR_AUDIT', row);
    commit('SET_AUDIT_SIDEBAR_OPEN', true);
    commit('RESET_AUDIT_FILTERS');
    dispatch('FETCH_ROW_AUDIT_LOGS', rowId);
  },

  /**
   * Close the audit sidebar
   */
  CLOSE_AUDIT_SIDEBAR({ commit }) {
    commit('SET_AUDIT_SIDEBAR_OPEN', false);
    commit('CLEAR_AUDIT_STATE');
  },

  /**
   * Toggle audit view mode between summary and detail
   */
  SET_AUDIT_VIEW_MODE({ commit }, mode) {
    commit('SET_AUDIT_VIEW_MODE', mode);
  },

  /**
   * Update audit filters and refresh data
   */
  async UPDATE_AUDIT_FILTERS({ commit, state, dispatch }, filters) {
    commit('SET_AUDIT_FILTERS', filters);

    // Use auditRegisterId which is set when opening the sidebar
    if (state.auditSidebarMode === 'register' && state.auditRegisterId) {
      await dispatch('FETCH_REGISTER_AUDIT_LOGS', state.auditRegisterId);
    } else if (state.auditSidebarMode === 'row' && state.selectedRowForAudit) {
      // Note: row.Id uses capital I (from API response)
      const rowId = state.selectedRowForAudit.Id || state.selectedRowForAudit.id;
      await dispatch('FETCH_ROW_AUDIT_LOGS', rowId);
    }
  },

  /**
   * Refresh current audit logs based on mode
   */
  async REFRESH_AUDIT_LOGS({ state, dispatch }, { registerId, rowId }) {
    if (state.auditSidebarMode === 'register' && registerId) {
      await dispatch('FETCH_REGISTER_AUDIT_LOGS', registerId);
    } else if (state.auditSidebarMode === 'row' && rowId) {
      await dispatch('FETCH_ROW_AUDIT_LOGS', rowId);
    }
  },

  // ==========================================
  // API DATA SOURCE ACTIONS
  // ==========================================

  async FETCH_API_DATA_SOURCES({ commit }, params) {
    commit('SET_API_DATA_SOURCE_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const result = await apiDataSourceService.getDataSources(params);
      commit('SET_API_DATA_SOURCES', result.items || result);
      commit('SET_API_DATA_SOURCES_TOTAL', result.total || (Array.isArray(result) ? result.length : 0));
      return result;
    } catch (err) {
      commit('SET_ERROR', err);
      throw err;
    } finally {
      commit('SET_API_DATA_SOURCE_LOADING', false);
    }
  },

  async FETCH_API_DATA_SOURCE({ commit }, id) {
    commit('SET_API_DATA_SOURCE_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const dataSource = await apiDataSourceService.getDataSource(id);
      commit('SET_SELECTED_API_DATA_SOURCE', dataSource);
      return dataSource;
    } catch (err) {
      commit('SET_ERROR', err);
      throw err;
    } finally {
      commit('SET_API_DATA_SOURCE_LOADING', false);
    }
  },

  async CREATE_API_DATA_SOURCE({ commit }, data) {
    commit('SET_API_DATA_SOURCE_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const created = await apiDataSourceService.createDataSource(data);
      commit('ADD_API_DATA_SOURCE', created);
      return created;
    } catch (err) {
      commit('SET_ERROR', err);
      throw err;
    } finally {
      commit('SET_API_DATA_SOURCE_LOADING', false);
    }
  },

  async UPDATE_API_DATA_SOURCE({ commit }, { id, data }) {
    commit('SET_API_DATA_SOURCE_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const updated = await apiDataSourceService.updateDataSource(id, data);
      commit('UPDATE_API_DATA_SOURCE', updated);
      return updated;
    } catch (err) {
      commit('SET_ERROR', err);
      throw err;
    } finally {
      commit('SET_API_DATA_SOURCE_LOADING', false);
    }
  },

  async DELETE_API_DATA_SOURCE({ commit }, id) {
    commit('SET_API_DATA_SOURCE_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      await apiDataSourceService.deleteDataSource(id);
      commit('REMOVE_API_DATA_SOURCE', id);
    } catch (err) {
      commit('SET_ERROR', err);
      throw err;
    } finally {
      commit('SET_API_DATA_SOURCE_LOADING', false);
    }
  },

  async TEST_API_CONNECTION({ commit }, connectionConfig) {
    commit('SET_API_WIZARD_TEST_RESULT', null);
    commit('CLEAR_ERROR');

    try {
      const result = await apiDataSourceService.testConnection(connectionConfig);
      commit('SET_API_WIZARD_TEST_RESULT', result);
      return result;
    } catch (err) {
      commit('SET_ERROR', err);
      throw err;
    }
  },

  async FETCH_API_PREVIEW({ commit }, { connectionConfig, maxRows, signal }) {
    commit('SET_API_WIZARD_PREVIEW_DATA', null);
    commit('CLEAR_ERROR');

    try {
      const result = await apiDataSourceService.fetchPreview(connectionConfig, maxRows, { signal });
      commit('SET_API_WIZARD_PREVIEW_DATA', result);
      return result;
    } catch (err) {
      commit('SET_ERROR', err);
      throw err;
    }
  },

  async TRIGGER_MANUAL_PULL({ commit }, id) {
    commit('SET_API_DATA_SOURCE_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const result = await apiDataSourceService.triggerManualPull(id);
      return result;
    } catch (err) {
      commit('SET_ERROR', err);
      throw err;
    } finally {
      commit('SET_API_DATA_SOURCE_LOADING', false);
    }
  },

  async TOGGLE_API_DATA_SOURCE_SCHEDULE({ commit }, { id, enabled }) {
    commit('SET_API_DATA_SOURCE_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const result = await apiDataSourceService.toggleSchedule(id, enabled);
      commit('UPDATE_API_DATA_SOURCE', result);
      return result;
    } catch (err) {
      commit('SET_ERROR', err);
      throw err;
    } finally {
      commit('SET_API_DATA_SOURCE_LOADING', false);
    }
  },

  async FETCH_EXECUTION_HISTORY({ commit }, { dataSourceId, params }) {
    commit('SET_EXECUTION_HISTORY_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const result = await apiDataSourceService.getExecutionHistory(dataSourceId, params);
      commit('SET_EXECUTION_HISTORY', result.items || result);
      commit('SET_EXECUTION_HISTORY_TOTAL', result.total || 0);
      return result;
    } catch (err) {
      commit('SET_ERROR', err);
      throw err;
    } finally {
      commit('SET_EXECUTION_HISTORY_LOADING', false);
    }
  },

  // API Wizard navigation
  SET_API_WIZARD_STEP({ commit }, step) {
    commit('SET_API_WIZARD_STEP', step);
  },

  OPEN_API_WIZARD({ commit }, { mode = 'create', dataSource = null } = {}) {
    commit('RESET_API_WIZARD');
    commit('SET_API_WIZARD_MODE', mode);
    if (dataSource) {
      commit('SET_SELECTED_API_DATA_SOURCE', dataSource);
      commit('SET_API_WIZARD_CONFIG', {
        name: dataSource.name,
        url: dataSource.url,
        httpMethod: dataSource.httpMethod,
        requestBody: dataSource.requestBody || '',
        headers: dataSource.headers || [],
        auth: dataSource.authConfig || { authType: 0 },
        responseDataPath: dataSource.responseDataPath || '',
        pagination: dataSource.paginationConfig || { type: 0 },
      });
      if (dataSource.fieldMapping) {
        commit('SET_API_WIZARD_MAPPING', dataSource.fieldMapping);
      }
      if (dataSource.scheduleConfig) {
        commit('SET_API_WIZARD_SCHEDULE', dataSource.scheduleConfig);
      }
    }
    commit('SET_API_WIZARD_OPEN', true);
  },

  CLOSE_API_WIZARD({ commit }) {
    commit('SET_API_WIZARD_OPEN', false);
    commit('RESET_API_WIZARD');
  },
};
